/**
 * Detail jurnal per TRANSAKSI — tujuan tautan "Lihat Jurnal".
 *
 * Buku Besar (`FinanceBukuBesar.jsx`) bersumbu satu AKUN; halaman ini
 * bersumbu satu TRANSAKSI: semua sisi debit & kreditnya sekaligus, apakah
 * seimbang, dan jalan pulang ke dokumen yang melahirkannya. Sebelum ini
 * tautan "Lihat Jurnal" di Beban Marketing mendarat di `/finance/kas-bank`
 * tanpa filter apa pun — pengguna disuruh mencari sendiri (§6
 * KEPUTUSAN-TERTUNDA).
 *
 * SATU KOMPONEN, DUA MODE:
 *   /finance/jurnal                    → kotak cari + pemindai "sumber tidak dikenali"
 *   /finance/jurnal/:transactionId     → detail satu transaksi
 * Digabung karena keduanya berbagi gerbang izin (`finance_laporan.view`,
 * SAMA dengan Buku Besar & `require_permission` di `buku_besar.py`) dan
 * karena hasil pemindaian tidak ada gunanya tanpa bisa langsung diklik ke
 * detailnya.
 *
 * TAUTAN KE DOKUMEN SUMBER DIGERBANGI DUA KALI: backend mengirim
 * `izin_modul` milik dokumen itu, dan tombolnya hanya dirender kalau
 * `can(izin_modul, "view")` — kalau tidak, teksnya tetap tampil tapi tidak
 * bisa diklik. Tanpa itu, staf yang boleh melihat laporan tapi tidak boleh
 * membuka Utang akan menekan tautan dan mendarat di 403.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, BookOpen, Scale, Search, ExternalLink, RotateCcw, HelpCircle,
  FileSearch, Link2, AlertTriangle, CheckCircle2, Layers,
} from "lucide-react";
import { api, idr } from "../../lib/api";
import { formatTanggal } from "../../lib/tanggal";
import { useAuth } from "../../context/AuthContext";

const LIMIT_PINDAI = 100;

export default function FinanceJurnalDetail() {
  const { transactionId } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const bolehLihat = can("finance_laporan", "view");

  const [cari, setCari] = useState("");

  if (!bolehLihat) {
    return (
      <div className="p-4 md:p-8 max-w-3xl" data-testid="jurnal-detail-no-akses">
        <p className="text-sm text-slate-500">
          Anda tidak punya izin melihat laporan keuangan (<code>finance_laporan</code>).
        </p>
      </div>
    );
  }

  const submitCari = (e) => {
    e.preventDefault();
    const t = cari.trim();
    if (!t) return;
    navigate(`/finance/jurnal/${encodeURIComponent(t)}`);
  };

  return (
    <div className="p-4 md:p-8 max-w-[1400px] space-y-6" data-testid="finance-jurnal-detail">
      <header>
        <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Laporan &amp; Kontrol</p>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1 inline-flex items-center gap-2">
          <BookOpen className="w-6 h-6 text-emerald-600" /> Jurnal per Transaksi
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Semua sisi debit &amp; kredit satu transaksi, apakah seimbang, dan dokumen sumbernya.
        </p>
      </header>

      <form onSubmit={submitCari} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex flex-wrap items-end gap-3">
        <label className="block flex-1 min-w-[280px]">
          <span className="text-xs font-medium text-slate-600">Nomor transaksi jurnal</span>
          <input
            value={cari} onChange={(e) => setCari(e.target.value)}
            data-testid="jurnal-cari-input"
            placeholder="Tempel nomor transaksi di sini…"
            className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm font-mono"
          />
        </label>
        <button type="submit" data-testid="jurnal-cari-submit"
          className="h-10 px-5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2">
          <Search className="w-4 h-4" /> Buka
        </button>
        {transactionId && (
          <Link to="/finance/jurnal" data-testid="jurnal-kembali-pindai"
            className="h-10 px-4 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Pemindai
          </Link>
        )}
      </form>

      {transactionId ? <Detail transactionId={transactionId} can={can} /> : <Pindai />}
    </div>
  );
}

/* ========================= Mode 1: detail satu transaksi ==================== */

function Detail({ transactionId, can }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let batal = false;
    setLoading(true); setError(null); setData(null);
    api.get(`/finance/jurnal/${encodeURIComponent(transactionId)}`)
      .then((r) => { if (!batal) setData(r.data); })
      .catch((e) => { if (!batal) setError(e?.response?.data?.detail || "Gagal memuat jurnal"); })
      .finally(() => { if (!batal) setLoading(false); });
    return () => { batal = true; };
  }, [transactionId]);

  if (loading) return <p className="text-sm text-slate-400 text-center py-12" data-testid="jurnal-loading">Memuat jurnal…</p>;
  if (error) {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-xl p-5" data-testid="jurnal-error">
        <p className="text-sm text-rose-800">{error}</p>
        <p className="text-[11px] font-mono text-rose-500 mt-2">{transactionId}</p>
      </div>
    );
  }
  if (!data) return null;

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kartu label="Total Debit" value={idr(data.total_debit)} tint="sky" />
        <Kartu label="Total Kredit" value={idr(data.total_kredit)} tint="amber" />
        <Kartu
          label="Keseimbangan"
          value={data.seimbang ? "SEIMBANG" : `SELISIH ${idr(Math.abs(data.selisih))}`}
          tint={data.seimbang ? "emerald" : "rose"}
          icon={Scale}
          testid="jurnal-seimbang"
        />
        {/* "kelompok", bukan "posting": buku besar tidak menyimpan batas
            antar-posting, jadi jumlah posting bukan angka yang bisa
            dipertanggungjawabkan halaman ini (lihat `_kelompokkan` di
            buku_besar.py). Yang dihitung adalah kelompok
            tanggal + deskripsi + pembalikan. */}
        <Kartu label="Baris Jurnal" value={`${data.jumlah_baris} baris · ${data.jumlah_kelompok} kelompok`}
          tint="slate" testid="jurnal-jumlah-baris" />
      </div>

      {/* Nada sengaja datar, bukan alarm: ini pagar untuk transaksi yang
          sangat panjang, bukan tanda ada yang rusak. Angka total & vonis
          keseimbangan di kartu atas SUDAH dihitung dari seluruh baris. */}
      {data.terpotong && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-xs text-slate-600 flex items-start gap-2"
          data-testid="jurnal-terpotong">
          <Layers className="w-3.5 h-3.5 shrink-0 mt-0.5 text-slate-400" />
          <span>
            Transaksi ini punya <b>{data.jumlah_baris.toLocaleString("id-ID")}</b> baris jurnal; daftar di
            bawah menampilkan <b>{data.jumlah_baris_ditampilkan.toLocaleString("id-ID")}</b> yang pertama.
            Total debit, total kredit, dan status keseimbangan di atas tetap dihitung dari{" "}
            <b>seluruh</b> baris — bukan dari yang tampil.
          </span>
        </div>
      )}

      <PanelSumber sumber={data.sumber} terkait={data.dokumen_terkait} can={can} />

      {data.ada_pembalikan && (
        <div className="bg-rose-50 border border-rose-200 rounded-lg px-4 py-2.5 text-xs text-rose-800 inline-flex items-center gap-2"
          data-testid="jurnal-ada-pembalikan">
          <RotateCcw className="w-3.5 h-3.5 shrink-0" />
          Transaksi ini punya jurnal pembalik. Jurnal asli tetap ditampilkan utuh di bawah — tidak dihapus,
          tidak digabung dengan pembaliknya.
        </div>
      )}

      {data.kelompok.map((k, i) => <TabelKelompok key={i} k={k} i={i} />)}

      <p className="text-[11px] font-mono text-slate-400" data-testid="jurnal-txn-id">
        Nomor transaksi: {data.transaction_id}
      </p>
    </>
  );
}

function PanelSumber({ sumber, terkait, can }) {
  if (!sumber?.ditemukan) {
    // Sengaja TIDAK berbunyi seperti tuduhan "jurnal yatim": di data produksi,
    // 6 dari 17 kelompok jurnal tidak punya dokumen `finance_transactions`
    // dan keenamnya tertaut sah ke koleksi lain. Alarm palsu membuat orang
    // berhenti membaca alarm yang benar.
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-5" data-testid="jurnal-sumber-tidak-dikenali">
        <p className="text-sm font-semibold text-amber-900 inline-flex items-center gap-2">
          <HelpCircle className="w-4 h-4" /> Dokumen sumber tidak dikenali
        </p>
        <p className="text-xs text-amber-800 mt-1.5 leading-relaxed">{sumber?.alasan}</p>
        {sumber?.source_module && (
          <p className="text-[11px] text-amber-700 mt-2">
            Modul yang tercatat di baris jurnal: <span className="font-mono">{sumber.source_module}</span>
          </p>
        )}
        {(sumber?.petunjuk || []).length > 0 && (
          <p className="text-[11px] text-amber-700 mt-1">
            Field penunjuk yang ada: <span className="font-mono">{sumber.petunjuk.join(", ")}</span>
          </p>
        )}
      </div>
    );
  }

  const bolehBuka = sumber.route && (!sumber.izin_modul || can(sumber.izin_modul, "view"));
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="jurnal-sumber">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Dokumen Sumber</p>
      <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-display font-semibold text-slate-900">
            {sumber.label}
            {sumber.kode && <span className="ml-2 font-mono text-sm text-slate-500">{sumber.kode}</span>}
          </p>
          <p className="text-sm text-slate-600 mt-0.5">{sumber.judul || "-"}</p>
          <p className="text-xs text-slate-400 mt-0.5">
            {sumber.tanggal ? formatTanggal(sumber.tanggal) : "-"}
            {sumber.nominal != null && ` · ${idr(sumber.nominal)}`}
            {sumber.status && ` · ${sumber.status}`}
          </p>
          {sumber.catatan && <p className="text-[11px] text-slate-500 mt-1.5 italic">{sumber.catatan}</p>}
        </div>
        {bolehBuka ? (
          <Link to={sumber.route} data-testid="jurnal-sumber-buka"
            className="h-9 px-4 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-sm font-medium inline-flex items-center gap-1.5 shrink-0">
            Buka dokumen <ExternalLink className="w-3.5 h-3.5" />
          </Link>
        ) : (
          <span className="text-[11px] text-slate-400 shrink-0" data-testid="jurnal-sumber-tanpa-tautan">
            {sumber.route ? "Anda tidak punya izin membuka halamannya" : "Tidak ada halaman khusus untuk dokumen ini"}
          </span>
        )}
      </div>

      {(terkait || []).length > 0 && (
        <div className="mt-4 pt-3 border-t border-slate-100">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Dokumen Terkait</p>
          <div className="flex flex-wrap gap-2">
            {terkait.map((t, i) => {
              const boleh = t.route && (!t.izin_modul || can(t.izin_modul, "view"));
              const isi = (
                <>
                  <Link2 className="w-3 h-3 shrink-0" />
                  {t.label}
                  {t.kode && <span className="font-mono">{t.kode}</span>}
                  {t.judul && <span className="text-slate-400 truncate max-w-[160px]">· {t.judul}</span>}
                </>
              );
              return boleh ? (
                <Link key={i} to={t.route} data-testid={`jurnal-terkait-${i}`}
                  className="inline-flex items-center gap-1.5 px-2.5 h-8 rounded-lg border border-slate-200 hover:bg-slate-50 text-xs text-slate-700">
                  {isi}
                </Link>
              ) : (
                <span key={i} data-testid={`jurnal-terkait-${i}`}
                  className="inline-flex items-center gap-1.5 px-2.5 h-8 rounded-lg border border-slate-200 bg-slate-50 text-xs text-slate-400">
                  {isi}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function TabelKelompok({ k, i }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto" data-testid={`jurnal-kelompok-${i}`}>
      <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between flex-wrap gap-2">
        <div className="min-w-0">
          <h3 className="font-display font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
            <span>{k.tanggal ? formatTanggal(k.tanggal) : "-"}</span>
            {k.is_reversal && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-rose-50 text-rose-700 border-rose-200"
                data-testid={`jurnal-kelompok-reversal-${i}`}>
                <RotateCcw className="w-3 h-3" /> pembalikan
              </span>
            )}
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">{k.deskripsi || "-"}</p>
          {k.terpotong && (
            <p className="text-[11px] text-slate-500 mt-1" data-testid={`jurnal-kelompok-terpotong-${i}`}>
              Menampilkan {k.jumlah_baris_ditampilkan} dari {k.jumlah_baris} baris; total di bawah
              tetap dihitung dari seluruhnya.
            </p>
          )}
          {k.salinan_identik > 1 && (
            <p className="text-[11px] text-slate-500 mt-1" data-testid={`jurnal-kelompok-salinan-${i}`}>
              Isinya {k.salinan_identik} salinan yang sama persis. Buku besar tidak mencatat batas
              antar-posting, jadi ini bisa satu posting yang diulang {k.salinan_identik} kali, bisa juga
              satu posting yang memang berisi baris kembar — bandingkan dengan dokumen sumbernya
              kalau perlu dipastikan.
            </p>
          )}
        </div>
        <span
          data-testid={`jurnal-kelompok-seimbang-${i}`}
          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${
            k.seimbang ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"
          }`}>
          {k.seimbang ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
          {k.seimbang ? "Seimbang" : `Selisih ${idr(Math.abs(k.selisih))}`}
        </span>
      </div>
      <table className="w-full text-sm min-w-[620px]">
        <thead>
          <tr className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
            <th className="text-left py-3 px-4">Akun</th>
            <th className="text-right py-3 px-4">Debit</th>
            <th className="text-right py-3 px-4">Kredit</th>
          </tr>
        </thead>
        <tbody>
          {k.baris.map((b, j) => (
            <tr key={j} className="border-t border-slate-100" data-testid={`jurnal-baris-${i}-${j}`}>
              <td className="py-2.5 px-4">
                <span className="font-mono text-[11px] text-slate-500 mr-2">{b.code || "—"}</span>
                <span className="text-slate-800">{b.name}</span>
              </td>
              <td className="py-2.5 px-4 text-right font-mono text-slate-700">{b.debit ? idr(b.debit) : "-"}</td>
              <td className="py-2.5 px-4 text-right font-mono text-slate-700">{b.credit ? idr(b.credit) : "-"}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-slate-200 bg-slate-50 font-semibold text-slate-900">
            <td className="py-2.5 px-4">Total</td>
            <td className="py-2.5 px-4 text-right font-mono">{idr(k.total_debit)}</td>
            <td className="py-2.5 px-4 text-right font-mono">{idr(k.total_kredit)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

/* ========== Mode 2: pemindai kelompok jurnal yang sumbernya tak dikenali ==== */

function Pindai() {
  const [dari, setDari] = useState("");
  const [sampai, setSampai] = useState("");
  const [hasil, setHasil] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sudahJalan, setSudahJalan] = useState(false);

  const jalankan = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/finance/jurnal-sumber/pindai", {
        params: { date_from: dari || undefined, date_to: sampai || undefined, limit: LIMIT_PINDAI },
      });
      setHasil(r.data);
      setSudahJalan(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memindai jurnal");
    } finally {
      setLoading(false);
    }
  }, [dari, sampai]);

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[240px]">
          <p className="font-display font-semibold text-slate-900 inline-flex items-center gap-2">
            <FileSearch className="w-4 h-4 text-emerald-600" /> Pemindai jurnal tanpa dokumen sumber
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Mencari kelompok jurnal yang nomor transaksinya tidak cocok dengan dokumen mana pun.
          </p>
        </div>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Dari Tanggal</span>
          <input type="date" value={dari} onChange={(e) => setDari(e.target.value)}
            data-testid="pindai-dari"
            className="mt-1 h-10 px-3 rounded-lg border border-slate-200 text-sm" />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Sampai Tanggal</span>
          <input type="date" value={sampai} onChange={(e) => setSampai(e.target.value)}
            data-testid="pindai-sampai"
            className="mt-1 h-10 px-3 rounded-lg border border-slate-200 text-sm" />
        </label>
        <button onClick={jalankan} disabled={loading} data-testid="pindai-jalankan"
          className="h-10 px-5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium disabled:opacity-60">
          {loading ? "Memindai…" : "Jalankan Pemindaian"}
        </button>
      </div>

      {!sudahJalan && (
        <p className="text-sm text-slate-400 text-center py-10" data-testid="pindai-belum">
          Kosongkan tanggal untuk memindai seluruh buku besar, atau persempit dulu rentangnya.
        </p>
      )}

      {hasil && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Kartu label="Kelompok Jurnal Diperiksa" value={hasil.diperiksa.toLocaleString("id-ID")} tint="slate" />
            <Kartu label="Sumbernya Dikenali" value={hasil.dikenali.toLocaleString("id-ID")} tint="emerald" />
            <Kartu label="Sumber Tidak Dikenali" value={hasil.tidak_dikenali.toLocaleString("id-ID")}
              tint={hasil.tidak_dikenali > 0 ? "amber" : "emerald"} testid="pindai-tidak-dikenali" />
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-xs text-slate-600 leading-relaxed"
            data-testid="pindai-catatan">
            {hasil.catatan}
          </div>

          {hasil.terpotong && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 text-xs text-amber-800"
              data-testid="pindai-terpotong">
              Jumlah kelompok jurnal melebihi batas pemindaian sekali jalan — persempit rentang tanggalnya.
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto" data-testid="pindai-tabel">
            <table className="w-full text-sm min-w-[780px]">
              <thead>
                <tr className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="text-left py-3 px-4">Tanggal</th>
                  <th className="text-left py-3 px-4">Deskripsi</th>
                  <th className="text-left py-3 px-4">Nomor Transaksi</th>
                  <th className="text-right py-3 px-4">Debit</th>
                  <th className="text-right py-3 px-4">Kredit</th>
                </tr>
              </thead>
              <tbody>
                {hasil.items.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center py-10 text-sm text-emerald-700" data-testid="pindai-kosong">
                      Semua kelompok jurnal pada rentang ini punya dokumen sumber yang dikenali.
                    </td>
                  </tr>
                )}
                {hasil.items.map((it) => (
                  <tr key={it.transaction_id} className="border-t border-slate-100 hover:bg-slate-50"
                    data-testid={`pindai-baris-${it.transaction_id}`}>
                    <td className="py-2.5 px-4 whitespace-nowrap text-slate-800">
                      {it.tanggal_pertama ? formatTanggal(it.tanggal_pertama) : "-"}
                    </td>
                    <td className="py-2.5 px-4 text-slate-700">
                      <span className="block truncate max-w-[320px]">{it.deskripsi || "-"}</span>
                      {it.source_module && <span className="text-[10px] text-slate-400">{it.source_module}</span>}
                      {!it.seimbang && (
                        <span className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                          <AlertTriangle className="w-2.5 h-2.5" /> tidak seimbang
                        </span>
                      )}
                    </td>
                    <td className="py-2.5 px-4">
                      <Link to={`/finance/jurnal/${encodeURIComponent(it.transaction_id)}`}
                        data-testid={`pindai-buka-${it.transaction_id}`}
                        className="inline-flex items-center gap-1 font-mono text-[11px] text-emerald-700 hover:underline">
                        {it.transaction_id.slice(0, 12)}… <ExternalLink className="w-2.5 h-2.5" />
                      </Link>
                    </td>
                    <td className="py-2.5 px-4 text-right font-mono text-slate-700">{idr(it.total_debit)}</td>
                    <td className="py-2.5 px-4 text-right font-mono text-slate-700">{idr(it.total_kredit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {hasil.tidak_dikenali > hasil.count && (
              <p className="px-5 py-3 border-t border-slate-100 text-xs text-slate-500">
                Menampilkan {hasil.count} dari {hasil.tidak_dikenali} — persempit rentang tanggal untuk melihat sisanya.
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/* ================================ potongan UI ============================== */

// Kelas Tailwind ditulis LENGKAP per varian (bukan `bg-${tint}-50`) — JIT
// memindai kode sumber sebagai teks, kelas yang dirangkai saat runtime tidak
// pernah ikut ter-generate dan warnanya hilang di build produksi.
const TINT = {
  slate: "bg-slate-50 text-slate-600",
  sky: "bg-sky-50 text-sky-600",
  amber: "bg-amber-50 text-amber-600",
  emerald: "bg-emerald-50 text-emerald-600",
  rose: "bg-rose-50 text-rose-600",
};

function Kartu({ label, value, tint = "slate", icon: Icon, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid={testid}>
      <div className="flex items-center gap-2">
        {Icon && (
          <span className={`w-8 h-8 rounded-lg grid place-items-center ${TINT[tint] || TINT.slate}`}>
            <Icon className="w-4 h-4" />
          </span>
        )}
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      </div>
      <p className="text-lg font-display font-bold text-slate-900 mt-1.5 break-words">{value}</p>
    </div>
  );
}
